import type {
  JsonSchemaNode,
  JsonSchemaPrimitiveType,
  MarketplaceCode,
  ProductTypeDefinitionResponse,
  SettingsTemplate,
} from "./types";

export type PtdFieldContainer = "direct" | "arrayPrimitive" | "arrayObject" | "object";

export interface PtdEnumOption {
  value: string | number | boolean;
  label: string;
}

export interface PtdField {
  id: string;
  attributeKey: string;
  subKey?: string;
  container: PtdFieldContainer;
  label: string;
  description?: string;
  groupKey: string;
  groupTitle: string;
  required: boolean;
  valueType: JsonSchemaPrimitiveType;
  enumOptions: PtdEnumOption[];
  defaultValue?: unknown;
  defaultSiblings: Record<string, unknown>;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  format?: string;
}

export interface PtdFieldModel {
  fields: PtdField[];
  requiredFields: PtdField[];
  complexRequiredAttributes: string[];
}

const SIMPLE_TYPES = new Set<JsonSchemaPrimitiveType>([
  "string",
  "number",
  "integer",
  "boolean",
]);

const SERVER_OWNED_ATTRIBUTES = new Set([
  "condition_type",
  "fulfillment_availability",
  "merchant_shipping_group",
  "merchant_suggested_asin",
  "purchasable_offer",
  "quantity",
  "inventory",
  "price",
  "prices",
  "shipping",
  "shipping_template",
  "shipping_templates",
  "fulfillment_mode",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasSchemaProperties(value: unknown): value is JsonSchemaNode {
  return isRecord(value) && isRecord(value.properties);
}

export function resolveProductTypeSchema(
  definition: ProductTypeDefinitionResponse | null,
): JsonSchemaNode | null {
  if (!definition) return null;
  const nestedDefinition = isRecord(definition.definition) ? definition.definition : null;
  const candidates: unknown[] = [
    definition.resolvedSchema,
    definition.schemaDefinition,
    isRecord(definition.schema) ? definition.schema.resolvedSchema : undefined,
    nestedDefinition?.resolvedSchema,
    nestedDefinition?.schema,
    definition.schema,
    definition,
  ];
  return candidates.find(hasSchemaProperties) ?? null;
}

export function getProductTypeSchemaLink(
  definition: ProductTypeDefinitionResponse | null,
): string | null {
  if (!definition) return null;
  const candidates = [
    definition.schema,
    isRecord(definition.definition) ? definition.definition.schema : undefined,
  ];
  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;
    const resource =
      typeof candidate.link === "string"
        ? candidate.link
        : isRecord(candidate.link)
          ? candidate.link.resource
          : undefined;
    if (typeof resource === "string" && resource.startsWith("https://")) return resource;
  }
  return null;
}

function schemaType(node: JsonSchemaNode): JsonSchemaPrimitiveType | "array" | "object" | null {
  const raw = Array.isArray(node.type)
    ? node.type.find((entry) => entry !== "null")
    : node.type;
  if (
    raw === "string" ||
    raw === "number" ||
    raw === "integer" ||
    raw === "boolean" ||
    raw === "array" ||
    raw === "object"
  ) {
    return raw;
  }
  const enumValue = node.enum?.find(
    (entry) => entry !== null && entry !== undefined,
  );
  if (typeof enumValue === "string") return "string";
  if (typeof enumValue === "number") return Number.isInteger(enumValue) ? "integer" : "number";
  if (typeof enumValue === "boolean") return "boolean";
  if (typeof node.const === "string") return "string";
  if (typeof node.const === "number") return Number.isInteger(node.const) ? "integer" : "number";
  if (typeof node.const === "boolean") return "boolean";
  if (node.properties) return "object";
  if (node.items) return "array";
  return null;
}

function resolveLocalRef(node: JsonSchemaNode, root: JsonSchemaNode): JsonSchemaNode {
  if (!node.$ref?.startsWith("#/")) return node;
  const path = node.$ref
    .slice(2)
    .split("/")
    .map((segment) => segment.replaceAll("~1", "/").replaceAll("~0", "~"));
  let current: unknown = root;
  for (const segment of path) {
    if (!isRecord(current)) return node;
    current = current[segment];
  }
  return isRecord(current) ? ({ ...current, ...node, $ref: undefined } as JsonSchemaNode) : node;
}

function normalizeAlternatives(node: JsonSchemaNode, root: JsonSchemaNode): JsonSchemaNode {
  const resolved = resolveLocalRef(node, root);
  const alternatives = resolved.oneOf ?? resolved.anyOf;
  if (!alternatives?.length) return resolved;
  const normalized = alternatives.map((entry) =>
    normalizeAlternatives(resolveLocalRef(entry, root), root),
  );
  const types = normalized.map(schemaType).filter(Boolean);
  const commonType = types[0];
  if (!commonType || !types.every((entry) => entry === commonType)) return resolved;
  const enumValues = normalized.flatMap((entry) =>
    entry.enum ?? (entry.const !== undefined ? [entry.const] : []),
  );
  const enumNames = normalized.flatMap((entry) =>
    entry.enumNames ??
    (entry.const !== undefined && entry.title ? [entry.title] : []),
  );
  return {
    ...normalized[0],
    ...resolved,
    type: commonType,
    enum: enumValues.length ? enumValues : resolved.enum,
    enumNames: enumNames.length === enumValues.length ? enumNames : resolved.enumNames,
  };
}

function primitiveType(
  node: JsonSchemaNode,
  root: JsonSchemaNode,
): JsonSchemaPrimitiveType | null {
  const type = schemaType(normalizeAlternatives(node, root));
  return type && SIMPLE_TYPES.has(type as JsonSchemaPrimitiveType)
    ? (type as JsonSchemaPrimitiveType)
    : null;
}

function automaticValue(node: JsonSchemaNode): unknown {
  if (node.const !== undefined) return node.const;
  if (node.default !== undefined) return node.default;
  if (node.enum?.length === 1) return node.enum[0];
  return undefined;
}

function enumOptions(node: JsonSchemaNode): PtdEnumOption[] {
  if (!node.enum?.length) return [];
  return node.enum.flatMap((value, index) => {
    if (
      typeof value !== "string" &&
      typeof value !== "number" &&
      typeof value !== "boolean"
    ) {
      return [];
    }
    return [
      {
        value,
        label: node.enumNames?.[index] ?? String(value),
      },
    ];
  });
}

function humanizeKey(value: string) {
  return value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function groupsFromDefinition(
  definition: ProductTypeDefinitionResponse,
): Record<string, { title?: string; propertyNames?: string[] }> {
  if (definition.propertyGroups) return definition.propertyGroups;
  if (isRecord(definition.definition) && isRecord(definition.definition.propertyGroups)) {
    return definition.definition.propertyGroups as Record<
      string,
      { title?: string; propertyNames?: string[] }
    >;
  }
  return {};
}

function groupForAttribute(
  groups: Record<string, { title?: string; propertyNames?: string[] }>,
  attributeKey: string,
) {
  for (const [groupKey, group] of Object.entries(groups)) {
    if (group.propertyNames?.includes(attributeKey)) {
      return { groupKey, groupTitle: group.title ?? humanizeKey(groupKey) };
    }
  }
  return { groupKey: "other", groupTitle: "Weitere Amazon-Attribute" };
}

function fieldFromNode({
  attributeKey,
  subKey,
  container,
  node,
  root,
  required,
  groupKey,
  groupTitle,
  defaultSiblings = {},
  parentTitle,
}: {
  attributeKey: string;
  subKey?: string;
  container: PtdFieldContainer;
  node: JsonSchemaNode;
  root: JsonSchemaNode;
  required: boolean;
  groupKey: string;
  groupTitle: string;
  defaultSiblings?: Record<string, unknown>;
  parentTitle?: string;
}): PtdField | null {
  const normalized = normalizeAlternatives(node, root);
  const valueType = primitiveType(normalized, root);
  if (!valueType) return null;
  const auto = automaticValue(normalized);
  const hidden =
    normalized.hidden === true ||
    normalized.editable === false ||
    normalized.readOnly === true;
  if (
    hidden ||
    (subKey &&
      auto !== undefined &&
      (normalized.const !== undefined ||
        normalized.default !== undefined ||
        normalized.enum?.length === 1))
  ) {
    return null;
  }
  const ownTitle = normalized.title ?? (subKey ? humanizeKey(subKey) : humanizeKey(attributeKey));
  const label = subKey && parentTitle && ownTitle !== parentTitle ? `${parentTitle} · ${ownTitle}` : ownTitle;
  return {
    id: subKey ? `${attributeKey}.${subKey}` : attributeKey,
    attributeKey,
    subKey,
    container,
    label,
    description: normalized.description,
    groupKey,
    groupTitle,
    required,
    valueType,
    enumOptions: enumOptions(normalized),
    defaultValue: auto,
    defaultSiblings,
    minimum: normalized.minimum,
    maximum: normalized.maximum,
    minLength: normalized.minLength,
    maxLength: normalized.maxLength,
    format: normalized.format,
  };
}

export function buildPtdFieldModel(
  definition: ProductTypeDefinitionResponse,
  schema: JsonSchemaNode,
): PtdFieldModel {
  const root = resolveLocalRef(schema, schema);
  const properties = root.properties ?? {};
  const rootRequired = new Set(root.required ?? []);
  const groups = groupsFromDefinition(definition);
  const fields: PtdField[] = [];
  const representedRequired = new Set<string>();

  for (const [attributeKey, rawProperty] of Object.entries(properties)) {
    if (SERVER_OWNED_ATTRIBUTES.has(attributeKey.toLowerCase())) {
      if (rootRequired.has(attributeKey)) representedRequired.add(attributeKey);
      continue;
    }
    const property = normalizeAlternatives(resolveLocalRef(rawProperty, root), root);
    const propertyType = schemaType(property);
    const required = rootRequired.has(attributeKey);
    const group = groupForAttribute(groups, attributeKey);

    if (primitiveType(property, root)) {
      const field = fieldFromNode({
        attributeKey,
        container: "direct",
        node: property,
        root,
        required,
        ...group,
      });
      if (field) {
        fields.push(field);
        if (required) representedRequired.add(attributeKey);
      }
      continue;
    }

    if (propertyType === "array" && property.items) {
      const items = normalizeAlternatives(resolveLocalRef(property.items, root), root);
      if (primitiveType(items, root)) {
        const field = fieldFromNode({
          attributeKey,
          container: "arrayPrimitive",
          node: items,
          root,
          required,
          ...group,
          parentTitle: property.title,
        });
        if (field) {
          fields.push(field);
          if (required) representedRequired.add(attributeKey);
        }
        continue;
      }
      if (schemaType(items) === "object" && items.properties) {
        const childRequired = new Set(items.required ?? []);
        const defaults = Object.fromEntries(
          Object.entries(items.properties).flatMap(([childKey, childNode]) => {
            const normalizedChild = normalizeAlternatives(
              resolveLocalRef(childNode, root),
              root,
            );
            const value = automaticValue(normalizedChild);
            return value === undefined ? [] : [[childKey, value]];
          }),
        );
        let representedRequirement = false;
        for (const [childKey, childNode] of Object.entries(items.properties)) {
          const field = fieldFromNode({
            attributeKey,
            subKey: childKey,
            container: "arrayObject",
            node: childNode,
            root,
            required: required && childRequired.has(childKey),
            ...group,
            defaultSiblings: defaults,
            parentTitle: property.title,
          });
          if (field) {
            fields.push(field);
            if (field.required) representedRequirement = true;
          }
        }
        if (required && representedRequirement) representedRequired.add(attributeKey);
        continue;
      }
    }

    if (propertyType === "object" && property.properties) {
      const childRequired = new Set(property.required ?? []);
      const defaults = Object.fromEntries(
        Object.entries(property.properties).flatMap(([childKey, childNode]) => {
          const normalizedChild = normalizeAlternatives(
            resolveLocalRef(childNode, root),
            root,
          );
          const value = automaticValue(normalizedChild);
          return value === undefined ? [] : [[childKey, value]];
        }),
      );
      let representedRequirement = false;
      for (const [childKey, childNode] of Object.entries(property.properties)) {
        const field = fieldFromNode({
          attributeKey,
          subKey: childKey,
          container: "object",
          node: childNode,
          root,
          required: required && childRequired.has(childKey),
          ...group,
          defaultSiblings: defaults,
          parentTitle: property.title,
        });
        if (field) {
          fields.push(field);
          if (field.required) representedRequirement = true;
        }
      }
      if (required && representedRequirement) representedRequired.add(attributeKey);
    }
  }

  const groupRank = new Map(
    Object.keys(groups).map((groupKey, index) => [groupKey, index]),
  );
  fields.sort((left, right) => {
    if (left.required !== right.required) return left.required ? -1 : 1;
    const groupDiff =
      (groupRank.get(left.groupKey) ?? Number.MAX_SAFE_INTEGER) -
      (groupRank.get(right.groupKey) ?? Number.MAX_SAFE_INTEGER);
    if (groupDiff) return groupDiff;
    return left.label.localeCompare(right.label, "de");
  });

  return {
    fields,
    requiredFields: fields.filter((field) => field.required),
    complexRequiredAttributes: [...rootRequired].filter(
      (attributeKey) => !representedRequired.has(attributeKey),
    ),
  };
}

export function getPtdFieldValue(
  attributes: Record<string, unknown>,
  field: PtdField,
): unknown {
  const attribute = attributes[field.attributeKey];
  if (field.container === "direct") return attribute;
  if (field.container === "arrayPrimitive") {
    return Array.isArray(attribute) ? attribute[0] : undefined;
  }
  if (field.container === "arrayObject") {
    const first = Array.isArray(attribute) && isRecord(attribute[0]) ? attribute[0] : null;
    return field.subKey && first ? first[field.subKey] : undefined;
  }
  const object = isRecord(attribute) ? attribute : null;
  return field.subKey && object ? object[field.subKey] : undefined;
}

function isBlank(value: unknown) {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

export function setPtdFieldValue(
  attributes: Record<string, unknown>,
  field: PtdField,
  value: unknown,
): Record<string, unknown> {
  const next = { ...attributes };
  if (field.container === "direct") {
    if (isBlank(value)) delete next[field.attributeKey];
    else next[field.attributeKey] = value;
    return next;
  }
  if (field.container === "arrayPrimitive") {
    if (isBlank(value)) delete next[field.attributeKey];
    else next[field.attributeKey] = [value];
    return next;
  }
  if (field.container === "arrayObject") {
    const current = Array.isArray(next[field.attributeKey]) &&
      isRecord((next[field.attributeKey] as unknown[])[0])
      ? { ...((next[field.attributeKey] as unknown[])[0] as Record<string, unknown>) }
      : {};
    if (field.subKey) {
      if (isBlank(value)) delete current[field.subKey];
      else current[field.subKey] = value;
    }
    const merged = { ...field.defaultSiblings, ...current };
    if (Object.keys(merged).length) next[field.attributeKey] = [merged];
    else delete next[field.attributeKey];
    return next;
  }
  const current = isRecord(next[field.attributeKey])
    ? { ...(next[field.attributeKey] as Record<string, unknown>) }
    : {};
  if (field.subKey) {
    if (isBlank(value)) delete current[field.subKey];
    else current[field.subKey] = value;
  }
  const merged = { ...field.defaultSiblings, ...current };
  if (Object.keys(merged).length) next[field.attributeKey] = merged;
  else delete next[field.attributeKey];
  return next;
}

export function validatePtdFieldValue(field: PtdField, value: unknown): string | null {
  if (field.required && isBlank(value)) return "Pflichtfeld";
  if (isBlank(value)) return null;
  if (typeof value === "string") {
    if (field.minLength !== undefined && value.length < field.minLength) {
      return `Mindestens ${field.minLength} Zeichen`;
    }
    if (field.maxLength !== undefined && value.length > field.maxLength) {
      return `Höchstens ${field.maxLength} Zeichen`;
    }
  }
  if (typeof value === "number") {
    if (field.minimum !== undefined && value < field.minimum) {
      return `Mindestens ${field.minimum}`;
    }
    if (field.maximum !== undefined && value > field.maximum) {
      return `Höchstens ${field.maximum}`;
    }
  }
  return null;
}

export function missingRequiredPtdFields(
  model: PtdFieldModel,
  attributes: Record<string, unknown>,
): string[] {
  const missing = model.requiredFields
    .filter((field) => isBlank(getPtdFieldValue(attributes, field)))
    .map((field) => field.label);
  return [
    ...missing,
    ...model.complexRequiredAttributes
      .filter((attributeKey) => isBlank(attributes[attributeKey]))
      .map(humanizeKey),
  ];
}

export function getApplicableTemplateAttributes(
  template: SettingsTemplate,
  productType: string | undefined,
  marketplace: MarketplaceCode,
): Record<string, unknown> | null {
  if (template.kind !== "listing") return null;
  if (template.marketplace && template.marketplace !== marketplace) return null;
  if (!isRecord(template.values)) return null;
  const nestedMarketplace =
    typeof template.values.marketplace === "string"
      ? template.values.marketplace.toUpperCase()
      : undefined;
  if (nestedMarketplace && nestedMarketplace !== marketplace) return null;
  const templateProductType =
    typeof template.values.productType === "string"
      ? template.values.productType
      : typeof template.values.product_type === "string"
        ? template.values.product_type
        : undefined;
  if (
    templateProductType &&
    productType &&
    templateProductType.toUpperCase() !== productType.toUpperCase()
  ) {
    return null;
  }
  const attributes = template.values.attributes;
  if (!isRecord(attributes)) return null;
  const forbidden = new Set([
    "condition_type",
    "conditiontype",
    "fulfillment_availability",
    "fulfillmentavailability",
    "merchant_suggested_asin",
    "merchantsuggestedasin",
    "purchasable_offer",
    "purchasableoffer",
    "merchant_shipping_group",
    "merchantshippinggroup",
    "quantity",
    "inventory",
    "price",
    "prices",
    "start_price",
    "startprice",
    "start_prices",
    "startprices",
    "shipping",
    "shipping_template",
    "shippingtemplate",
    "shipping_templates",
    "shippingtemplates",
    "fulfillment_mode",
    "fulfillmentmode",
  ]);
  return Object.fromEntries(
    Object.entries(attributes).filter(([key]) => !forbidden.has(key.toLowerCase())),
  );
}
